import React, { useState, useEffect, useRef, HtmlHTMLAttributes } from 'react';

interface Company {
	id: string;
	companyHsId: string;
	name: string;
}

interface SelectCompanyProps {
	companiesList: Company[];
	makeSearch: (input: string) => void;
}

const SelectCompany: React.FC<SelectCompanyProps> = ({
	companiesList,
	makeSearch
}) => {
	const [searchInput, setSearchInput] = useState<string>('');
	const [companies, setCompanies] = useState<Company[]>(companiesList);
	const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
	const [companySelected, setCompanySelected] = useState<Company | null>(
		null
	);
	const [ignoreNextChange, setIgnoreNextChange] = useState<boolean>(false);
	const [showDropDown, setShowDropDown] = useState<boolean>(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLUListElement>(null);

	const handleSearchChange = (value: string) => {
		if (value !== companySelected?.name) {
			setCompanySelected(null);
		}

		setSearchInput(value);
		setShowDropDown(true);

		if (!value) {
			setShowDropDown(false);
			return;
		}

		const filtered = companies.filter(
			company =>
				company.name?.toLowerCase().includes(value.toLowerCase()) ||
				company?.companyHsId
					?.toLowerCase()
					.includes(value.toLowerCase())
		);

		setFilteredCompanies(filtered);
	};

	const handleSelectCompany = (company: Company) => {
		setShowDropDown(false);
		setIgnoreNextChange(true);
		setSearchInput(company.name);
		setCompanySelected(company);
	};

	const handleExecuteSearch = () => {
		if (!companySelected) {
			alert('Must select a company');
			return;
		}

		makeSearch(companySelected.id);
	};

	useEffect(() => {
		setCompanies(companiesList);
	}, [companiesList]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!(
					dropdownRef.current.contains(event.target as Node) ||
					inputRef.current?.contains(event.target as Node)
				)
			) {
				setShowDropDown(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	return (
		<div className="search-container">
			<input
				onFocus={() => setShowDropDown(true)}
				ref={inputRef}
				className="input-text-search-bar"
				type="text"
				id="company-search-input"
				autoComplete="off"
				value={searchInput}
				onChange={e => handleSearchChange(e.target.value)}
				placeholder="Search company by name"
			/>
			{showDropDown && filteredCompanies.length > 0 && (
				<ul className="companies-dropdown-list" ref={dropdownRef}>
					{filteredCompanies.map((company: Company) => (
						<li
							key={company.id}
							onClick={() => handleSelectCompany(company)}
						>
							{company.name}
						</li>
					))}
				</ul>
			)}

			<div className="admin-panel-search-buttons-box">
				<div
					className="admin-panel-search-button"
					onClick={() => handleExecuteSearch()}
				>
					Search
				</div>
			</div>
		</div>
	);
};

export default SelectCompany;
